// Onda 2 (2026-06-02): migrado pra Antd — Form + Input + Select + Button.
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Form, Input, Select, Button, Row, Col, Space, App as AntApp, InputNumber, Tooltip,
} from 'antd';
import { SaveOutlined, LockOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { isoToDatetimeLocal, datetimeLocalToIso } from '../../utils/dataFmt';
import {
  avisoResponsavel, camposResponsavelAoSalvar, opcoesResponsavel, valorInicialResponsavel,
} from '../../utils/responsavelNegocio';
import type { Negocio } from './types';

export function AbaEdit({ neg, vendedores, onSaved }: {
  neg: Negocio; vendedores: any[]; onSaved: () => void;
}) {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const [salvando, setSalvando] = useState(false);
  const [clientes, setClientes] = useState<any[]>([]);
  const [contatos, setContatos] = useState<any[]>([]);
  const [clienteIdSel, setClienteIdSel] = useState<string>(neg.cliente_id ? String(neg.cliente_id) : '');
  const vendedorChave = Form.useWatch('vendedor_chave', form);

  useEffect(() => {
    form.setFieldsValue({
      titulo: neg.titulo,
      cliente_id: neg.cliente_id ? String(neg.cliente_id) : undefined,
      contato_id: neg.contato_id ? String(neg.contato_id) : undefined,
      // Select do vendedor trabalha pelo e-mail — ver utils/responsavelNegocio.ts
      vendedor_chave: valorInicialResponsavel(neg),
      valor_estimado: neg.valor_estimado || 0,
      probabilidade: neg.probabilidade || 10,
      motivo_perda: neg.motivo_perda || '',
      observacoes: neg.observacoes || '',
      // L17 (2026-06-03): data_abertura editavel (importante pra licitacoes
      // manuais que nao recebem update do sync e pra ajustes em geral)
      data_abertura: isoToDatetimeLocal(neg.data_abertura),
    });
  }, [neg, form]);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${API_URL}/comercial/clientes`, { headers: { Authorization: token } });
        const data = res.data?.body ? JSON.parse(res.data.body) : res.data;
        setClientes(Array.isArray(data) ? data : []);
      } catch {}
    })();
  }, []);

  // Carrega contatos ao mudar cliente
  useEffect(() => {
    if (!clienteIdSel) { setContatos([]); return; }
    (async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${API_URL}/comercial/clientes/${clienteIdSel}/contatos`,
          { headers: { Authorization: token } });
        const data = res.data?.body ? JSON.parse(res.data.body) : res.data;
        setContatos(Array.isArray(data) ? data : []);
      } catch { setContatos([]); }
    })();
  }, [clienteIdSel]);

  async function salvar() {
    try {
      const values = await form.validateFields();
      setSalvando(true);
      const token = await getToken();
      // valor_estimado eh calculado automaticamente das propostas geradas ativas — nao enviamos
      await axios.put(`${API_URL}/comercial/propostas/${neg.id}`, {
        titulo: values.titulo,
        cliente_id: values.cliente_id ? parseInt(values.cliente_id) : null,
        contato_id: values.contato_id ? parseInt(values.contato_id) : null,
        // Responsável não alterado → não envia nada: o vendedor_email gravado fica
        // (antes, nome com grafia diferente do Cognito apagava o e-mail no 1º Salvar).
        ...camposResponsavelAoSalvar(neg, vendedores, values.vendedor_chave),
        probabilidade: parseInt(values.probabilidade) || 10,
        motivo_perda: values.motivo_perda || null,
        observacoes: values.observacoes,
        // L17 — converte de local volta pra ISO UTC; null se vazio
        data_abertura: values.data_abertura ? datetimeLocalToIso(values.data_abertura) : null,
      }, { headers: { Authorization: token } });
      message.success('Alterações salvas');
      onSaved();
    } catch (e: any) {
      if (e?.errorFields) return; // validacao
      message.error('Erro: ' + e.message);
    } finally { setSalvando(false); }
  }

  return (
    <Form form={form} layout="vertical" size="small" onFinish={salvar}>
      <Form.Item name="titulo" label="Título"
        rules={[{ required: true, message: 'Título obrigatório' }]}>
        <Input />
      </Form.Item>
      <Row gutter={12}>
        <Col xs={24} sm={12}>
          <Form.Item name="cliente_id" label="Empresa">
            <Select
              placeholder="Selecione"
              allowClear showSearch optionFilterProp="label"
              onChange={(v) => { setClienteIdSel(v || ''); form.setFieldsValue({ contato_id: undefined }); }}
              options={clientes.map(c => ({ label: c.razao_social, value: String(c.id) }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="contato_id" label="Contato">
            <Select
              placeholder={!clienteIdSel ? 'Selecione a empresa primeiro' : 'Selecione'}
              allowClear showSearch optionFilterProp="label"
              disabled={!clienteIdSel}
              options={contatos.map(ct => ({
                label: `${ct.nome}${ct.cargo ? ' — ' + ct.cargo : ''}${ct.decisor ? ' ★' : ''}`,
                value: String(ct.id),
              }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="vendedor_chave" label="Responsável (vendedor)"
            extra={avisoResponsavel(vendedores, vendedorChave)}>
            <Select
              placeholder="Selecione"
              allowClear showSearch optionFilterProp="label"
              options={opcoesResponsavel(neg, vendedores)}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label={
            <Space size={4}>
              <span>Valor estimado (R$)</span>
              <Tooltip title="Calculado automaticamente como SUM dos valores das propostas geradas ativas. Edite o quadro de serviços de cada proposta pra ajustar.">
                <LockOutlined style={{ color: '#888' }} />
              </Tooltip>
            </Space>
          }>
            <InputNumber readOnly disabled style={{ width: '100%' }}
              value={neg.valor_estimado || 0}
              formatter={(v) => `R$ ${Number(v).toLocaleString('pt-BR')}`} />
            <div style={{ fontSize: 10, color: '#888', marginTop: 3 }}>
              🔒 Calculado da soma das propostas geradas ativas
            </div>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="probabilidade" label="Probabilidade (%)"
            rules={[{ type: 'number', min: 0, max: 100, message: '0–100' }]}>
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        {/* L17 (2026-06-03): data_abertura editavel — crucial pra licitacoes */}
        <Col xs={24} sm={12}>
          <Form.Item name="data_abertura" label="Data de abertura (licitação)"
            extra="Para licitações: data da sessão de abertura. Editável manualmente — útil em prorrogações.">
            <Input type="datetime-local" />
          </Form.Item>
        </Col>
        {neg.status === 'perdido' && (
          <Col span={24}>
            <Form.Item name="motivo_perda" label="Motivo da perda">
              <Input />
            </Form.Item>
          </Col>
        )}
        <Col span={24}>
          <Form.Item name="observacoes" label="Observações">
            <Input.TextArea autoSize={{ minRows: 3, maxRows: 8 }} />
          </Form.Item>
        </Col>
      </Row>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={salvando}>
          Salvar alterações
        </Button>
      </div>
    </Form>
  );
}
